// HostelPanel — rooms with occupancy, allocate students to beds, vacate,
// residents list (with parent contact for emergencies).
import { useEffect, useState } from 'react';
import { Plus, Trash2, BedDouble, LogOut, Download } from 'lucide-react';
import { getHostelRooms, addHostelRoom, deleteHostelRoom, getHostelResidents, allocateHostel, vacateHostel } from '../api/client';
import Button from '../ui/Button';
import Badge from '../ui/Badge';
import { Card, CardHeader, StatTile } from '../ui/Card';
import { Field, Msg, Modal, errText, inputCls, theadRowCls, EmptyRow } from '../ui/Form';
import StudentPicker from './StudentPicker';
import { inr, fmtDate, downloadCsv } from '../ui/format';

const ROOM_EMPTY = { hostel_name: '', room_no: '', room_type: 'Boys', capacity: 4, monthly_fee: '' };

export default function HostelPanel({ onOpenStudent }) {
  const [data,      setData]      = useState(null);
  const [residents, setResidents] = useState([]);
  const [history,   setHistory]   = useState(false);
  const [roomForm,  setRoomForm]  = useState(null);
  const [alloc,     setAlloc]     = useState(null);   // { room_id, student, bed_no }
  const [msg,       setMsg]       = useState('');

  useEffect(() => { load(); }, [history]);
  async function load() {
    try {
      const [rooms, res] = await Promise.all([getHostelRooms(), getHostelResidents(history ? { history: 1 } : {})]);
      setData(rooms); setResidents(res);
    } catch (err) { setMsg(errText(err)); }
  }

  async function saveRoom(e) {
    e.preventDefault(); setMsg('');
    try { await addHostelRoom({ ...roomForm, capacity: Number(roomForm.capacity) }); setRoomForm(null); setMsg('Room added ✅'); load(); }
    catch (err) { setMsg(errText(err)); }
  }
  async function removeRoom(r) {
    if (!window.confirm(`Delete ${r.hostel_name} room ${r.room_no}?`)) return;
    try { await deleteHostelRoom(r.id); load(); } catch (err) { setMsg(errText(err)); }
  }
  async function saveAlloc(e) {
    e.preventDefault(); setMsg('');
    if (!alloc.student) return setMsg('Error: pick a student');
    try {
      await allocateHostel({ room_id: Number(alloc.room_id), student_id: alloc.student.id, bed_no: alloc.bed_no });
      setMsg(`${alloc.student.name} allotted ✅`); setAlloc(null); load();
    } catch (err) { setMsg(errText(err)); }
  }
  async function vacate(a) {
    if (!window.confirm(`Vacate ${a.student_name} from room ${a.room_no}?`)) return;
    try { await vacateHostel(a.id); setMsg(`${a.student_name} vacated`); load(); } catch (err) { setMsg(errText(err)); }
  }

  const freeRooms = data ? data.rooms.filter(r => r.free > 0) : [];
  const hostels = data ? [...new Set(data.rooms.map(r => r.hostel_name))] : [];

  return (
    <div>
      <div className="flex items-center justify-between gap-3 mb-4 flex-wrap">
        <h2 className="font-display font-extrabold text-xl text-ink">Hostel</h2>
        <div className="flex gap-2">
          <Button variant="secondary" onClick={() => setRoomForm(ROOM_EMPTY)} className="flex items-center gap-1.5"><Plus size={14} /> Add room</Button>
          <Button onClick={() => setAlloc({ room_id: freeRooms[0]?.id || '', student: null, bed_no: '' })} disabled={!freeRooms.length} className="flex items-center gap-1.5">
            <BedDouble size={14} /> Allot room
          </Button>
        </div>
      </div>
      <Msg msg={msg} className="mb-3" />

      {data && (
        <div className="grid grid-cols-[repeat(auto-fill,minmax(170px,1fr))] gap-3.5 mb-5">
          <StatTile tone="transit" label="Rooms" value={data.totals.rooms} caption={`${hostels.length} hostel${hostels.length === 1 ? '' : 's'}`} />
          <StatTile tone="clearance" label="Beds" value={data.totals.beds} />
          <StatTile tone="success" label="Occupied" value={data.totals.occupied} caption={data.totals.beds ? `${Math.round((data.totals.occupied / data.totals.beds) * 100)}% full` : null} />
          <StatTile tone="slate" label="Free beds" value={data.totals.free} />
        </div>
      )}

      {data && hostels.map(h => (
        <Card key={h} className="mb-4">
          <CardHeader title={h} />
          <div className="p-4 grid grid-cols-[repeat(auto-fill,minmax(180px,1fr))] gap-3">
            {data.rooms.filter(r => r.hostel_name === h).map(r => {
              const pct = Math.round((r.occupied / r.capacity) * 100);
              return (
                <div key={r.id} className="border border-line rounded-m p-3 bg-paper-raised">
                  <div className="flex items-center justify-between">
                    <b className="font-display text-ink">Room {r.room_no}</b>
                    <button onClick={() => removeRoom(r)} aria-label={`Delete room ${r.room_no}`} className="text-ink-soft hover:text-critical"><Trash2 size={13} /></button>
                  </div>
                  <div className="text-xs text-ink-soft mt-0.5">{r.room_type}{r.monthly_fee != null ? ` · ${inr(r.monthly_fee)}/month` : ''}</div>
                  <div className="h-1.5 bg-line-soft rounded-full mt-2 overflow-hidden"><div className={`h-full ${r.free ? 'bg-transit' : 'bg-critical'}`} style={{ width: `${pct}%` }} /></div>
                  <div className="text-xs font-bold mt-1 tabular-nums">{r.occupied}/{r.capacity} beds {r.free ? <span className="text-success">· {r.free} free</span> : <span className="text-critical">· full</span>}</div>
                </div>
              );
            })}
          </div>
        </Card>
      ))}
      {data && !data.rooms.length && <Card className="p-6 text-sm text-ink-soft mb-4">No rooms yet — click Add room.</Card>}

      <Card>
        <CardHeader title={history ? `All allotments (${residents.length})` : `Current residents (${residents.length})`} action={
          <div className="flex items-center gap-3">
            <label className="flex items-center gap-1.5 text-xs font-bold text-ink-soft"><input type="checkbox" checked={history} onChange={e => setHistory(e.target.checked)} className="accent-[var(--transit)]" /> Include past</label>
            <Button variant="secondary" className="!py-1.5 !text-xs flex items-center gap-1.5" disabled={!residents.length}
              onClick={() => downloadCsv('hostel-residents.csv', [
                { key: 'hostel_name', label: 'Hostel' }, { key: 'room_no', label: 'Room' }, { key: 'bed_no', label: 'Bed' },
                { key: 'student_name', label: 'Student' }, { key: 'class_name', label: 'Class' }, { key: 'guardian_name', label: 'Parent' },
                { key: 'guardian_phone', label: 'Phone' }, { key: 'from_date', label: 'From' }, { key: 'to_date', label: 'To' },
              ], residents)}><Download size={13} /> CSV</Button>
          </div>
        } />
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead><tr className={theadRowCls}><th className="px-4 py-2.5">Student</th><th className="px-4 py-2.5">Room</th><th className="px-4 py-2.5">Parent</th><th className="px-4 py-2.5">Since</th><th className="px-4 py-2.5"></th></tr></thead>
            <tbody>
              {residents.map(a => (
                <tr key={a.id} className="border-t border-line-soft">
                  <td className="px-4 py-3"><button onClick={() => onOpenStudent(a.student_id)} className="font-semibold text-transit-deep hover:underline">{a.student_name}</button>
                    <span className="block text-xs text-ink-soft">Class {a.class_name || '—'}</span></td>
                  <td className="px-4 py-3">{a.hostel_name} · {a.room_no}{a.bed_no ? ` · Bed ${a.bed_no}` : ''}</td>
                  <td className="px-4 py-3">{a.guardian_name || '—'}{a.guardian_phone && <a href={`tel:${a.guardian_phone}`} className="block text-xs text-transit-deep font-semibold">{a.guardian_phone}</a>}</td>
                  <td className="px-4 py-3 whitespace-nowrap">{fmtDate(a.from_date)}{a.to_date && <span className="block"><Badge tone="neutral">Left {fmtDate(a.to_date)}</Badge></span>}</td>
                  <td className="px-4 py-3 text-right">{!a.to_date && <Button variant="secondary" className="!px-2.5 !py-1 !text-xs flex items-center gap-1" onClick={() => vacate(a)}><LogOut size={12} /> Vacate</Button>}</td>
                </tr>
              ))}
              {!residents.length && <EmptyRow cols={5}>No residents</EmptyRow>}
            </tbody>
          </table>
        </div>
      </Card>

      {roomForm && (
        <Modal title="Add room" onClose={() => setRoomForm(null)} width={460}>
          <form onSubmit={saveRoom} className="flex flex-col gap-3">
            <div className="grid grid-cols-2 gap-3">
              <Field label="Hostel / block">
                <input required list="hostel-names" className={inputCls} placeholder="e.g. Tagore House" value={roomForm.hostel_name} onChange={e => setRoomForm({ ...roomForm, hostel_name: e.target.value })} />
                <datalist id="hostel-names">{hostels.map(h => <option key={h} value={h} />)}</datalist>
              </Field>
              <Field label="Room no."><input required className={inputCls} value={roomForm.room_no} onChange={e => setRoomForm({ ...roomForm, room_no: e.target.value })} /></Field>
            </div>
            <div className="grid grid-cols-3 gap-3">
              <Field label="For"><select className={inputCls} value={roomForm.room_type} onChange={e => setRoomForm({ ...roomForm, room_type: e.target.value })}>{['Boys', 'Girls', 'Staff', 'Mixed'].map(t => <option key={t}>{t}</option>)}</select></Field>
              <Field label="Beds"><input required type="number" min="1" className={inputCls} value={roomForm.capacity} onChange={e => setRoomForm({ ...roomForm, capacity: e.target.value })} /></Field>
              <Field label="Fee/month (₹)"><input type="number" min="0" step="any" className={inputCls} value={roomForm.monthly_fee} onChange={e => setRoomForm({ ...roomForm, monthly_fee: e.target.value })} /></Field>
            </div>
            <Button type="submit" className="w-fit">Add room</Button>
          </form>
        </Modal>
      )}

      {alloc && (
        <Modal title="Allot a room" onClose={() => setAlloc(null)} width={460}>
          <form onSubmit={saveAlloc} className="flex flex-col gap-3">
            <div className="flex flex-col gap-1 text-xs font-bold text-ink-soft">Student
              <StudentPicker value={alloc.student} onChange={s => setAlloc({ ...alloc, student: s })} />
            </div>
            <div className="grid grid-cols-[1fr_100px] gap-3">
              <Field label="Room">
                <select required className={inputCls} value={alloc.room_id} onChange={e => setAlloc({ ...alloc, room_id: e.target.value })}>
                  {freeRooms.map(r => <option key={r.id} value={r.id}>{r.hostel_name} · {r.room_no} ({r.room_type}, {r.free} free)</option>)}
                </select>
              </Field>
              <Field label="Bed"><input className={inputCls} placeholder="e.g. A" value={alloc.bed_no} onChange={e => setAlloc({ ...alloc, bed_no: e.target.value })} /></Field>
            </div>
            <p className="text-xs text-ink-soft">To charge the hostel fee, add it on the student's Fees tab (Adjust or Add charge) or as a fee head for the class.</p>
            <Button type="submit" className="w-fit">Allot</Button>
          </form>
        </Modal>
      )}
    </div>
  );
}
